import { useEffect, useMemo, useState } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight, Target, TrendingUp, TrendingDown, Loader2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  FinancialGoal,
  GOAL_CONFIG,
  GoalType,
  formatGoalValue,
  progressPct,
} from "@/lib/goals";
import { dgTrack } from "@/lib/dataGraph";

interface GoalImpactStripProps {
  goalType: GoalType;
  /** The value this tool run suggests the user is at right now (e.g. current savings, latest score, new rate). */
  proposedCurrentValue?: number | null;
  /** Optional target this tool suggests (only applied if no goal exists yet). */
  proposedTargetValue?: number | null;
  /** Slug describing where this update came from. Stored on the goal as last_source. */
  source: string;
  /** For metrics where lower is better (e.g. refi rate). */
  lowerIsBetter?: boolean;
  /** Optional small helper line shown under the headline. */
  helper?: string;
  /** Optional extra meta merged onto the goal row. */
  meta?: Record<string, unknown>;
}

/**
 * Renders a strip that shows how the latest tool result moved one of the user's
 * active financial goals. Anonymous users see a soft prompt to sign up.
 */
export default function GoalImpactStrip({
  goalType,
  proposedCurrentValue,
  proposedTargetValue,
  source,
  lowerIsBetter = false,
  helper,
  meta,
}: GoalImpactStripProps) {
  const { user } = useAuth();
  const [goal, setGoal] = useState<FinancialGoal | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const cfg = GOAL_CONFIG[goalType];

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("financial_goals")
        .select("*")
        .eq("user_id", user.id)
        .eq("goal_type", goalType)
        .eq("status", "active")
        .maybeSingle();
      if (!cancelled) {
        setGoal((data as unknown as FinancialGoal) ?? null);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, goalType]);

  const beforeValue = goal?.current_value ?? null;
  const afterValue =
    proposedCurrentValue !== null && proposedCurrentValue !== undefined && Number.isFinite(proposedCurrentValue)
      ? Number(proposedCurrentValue)
      : null;

  const delta = useMemo(() => {
    if (beforeValue === null || afterValue === null) return null;
    const raw = afterValue - Number(beforeValue);
    return lowerIsBetter ? -raw : raw;
  }, [beforeValue, afterValue, lowerIsBetter]);

  const hasMeaningfulMove =
    delta !== null && Math.abs(delta) >= (cfg.unit === "score" ? 1 : cfg.unit === "pct" ? 0.01 : 1);

  const beforePct = goal ? progressPct(goal) : 0;
  const afterPct = goal && afterValue !== null
    ? progressPct({ current_value: afterValue, target_value: goal.target_value })
    : beforePct;

  async function saveProgress() {
    if (!user) return;
    if (afterValue === null) return;
    setSaving(true);
    try {
      if (goal) {
        const { error } = await supabase
          .from("financial_goals")
          .update({
            current_value: afterValue,
            last_source: source,
            last_progress_at: new Date().toISOString(),
            meta: { ...(goal.meta ?? {}), ...(meta ?? {}) } as never,
          })
          .eq("id", goal.id);
        if (error) throw error;
        setGoal({ ...goal, current_value: afterValue, last_source: source });
        toast.success("Progress saved to your goal");
        dgTrack("financial", "goal_progress_updated", { goal_type: goalType, source, value: afterValue });
      } else {
        const target =
          proposedTargetValue !== null && proposedTargetValue !== undefined
            ? Number(proposedTargetValue)
            : null;
        const { data, error } = await supabase
          .from("financial_goals")
          .insert({
            user_id: user.id,
            goal_type: goalType,
            label: cfg.label,
            unit: cfg.unit,
            target_value: target,
            current_value: afterValue,
            status: "active",
            last_source: source,
            last_progress_at: new Date().toISOString(),
            meta: (meta ?? {}) as never,
          } as never)
          .select("*")
          .single();
        if (error) throw error;
        setGoal(data as unknown as FinancialGoal);
        toast.success("Tracking this as a goal");
        dgTrack("financial", "goal_created", { goal_type: goalType, source, value: afterValue });
      }
    } catch (e) {
      console.error("[GoalImpactStrip] save failed", e);
      toast.error("Couldn't save just now. Try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!user) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-primary mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-foreground text-sm">
              {cfg.icon} Track this toward a goal
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Sign up free to save this result to your <span className="font-medium">{cfg.label}</span> goal and watch your progress over time.
            </p>
          </div>
          <Button asChild size="sm" className="gap-1.5 shrink-0">
            <Link to="/auth">Sign up <ArrowRight className="h-3.5 w-3.5" /></Link>
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mt-6 rounded-2xl border border-border bg-muted/30 p-5 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking your goals…
      </div>
    );
  }

  // No matching active goal yet — invite the user to start tracking.
  if (!goal) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 rounded-2xl border border-primary/20 bg-card p-5 shadow-xs"
      >
        <div className="flex items-start gap-3">
          <div className="text-2xl leading-none">{cfg.icon}</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-0.5">
              Goal impact
            </p>
            <p className="font-semibold text-foreground text-sm">
              Start tracking <span className="text-primary">{cfg.label.toLowerCase()}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {helper ??
                `We'll seed this goal with your latest result${afterValue !== null ? ` (${formatGoalValue(afterValue, cfg.unit)})` : ""}${
                  proposedTargetValue ? ` and a target of ${formatGoalValue(proposedTargetValue, cfg.unit)}` : ""
                }.`}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {afterValue !== null && (
                <Button size="sm" onClick={saveProgress} disabled={saving} className="gap-1.5">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Target className="h-3.5 w-3.5" />}
                  Track this goal
                </Button>
              )}
              <Button asChild size="sm" variant="outline" className="gap-1.5">
                <Link to="/goals">All goals <ArrowRight className="h-3.5 w-3.5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  const movedForward = (delta ?? 0) > 0;
  const movedBackward = (delta ?? 0) < 0;
  const headline = !hasMeaningfulMove
    ? "Right on track"
    : movedForward
      ? `+${formatGoalValue(Math.abs(delta!), cfg.unit)} closer to your goal`
      : `${formatGoalValue(Math.abs(delta!), cfg.unit)} further from your goal`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-xs"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-0.5">
            Goal impact
          </p>
          <p className="font-semibold text-foreground text-sm flex items-center gap-1.5">
            <span className="text-base leading-none">{cfg.icon}</span>
            {goal.label}
          </p>
          {helper && <p className="text-xs text-muted-foreground mt-0.5">{helper}</p>}
        </div>
        <div className={`flex items-center gap-1 text-xs font-semibold ${
          movedForward ? "text-emerald-600" : movedBackward ? "text-amber-600" : "text-muted-foreground"
        }`}>
          {movedForward ? <TrendingUp className="h-3.5 w-3.5" /> : movedBackward ? <TrendingDown className="h-3.5 w-3.5" /> : null}
          {headline}
        </div>
      </div>

      <Progress value={afterPct} className="h-2" />
      <div className="flex justify-between text-[11px] text-muted-foreground mt-1.5">
        <span>
          Was {formatGoalValue(beforeValue, cfg.unit)} ({beforePct}%)
        </span>
        <span>
          {afterValue !== null ? `Now ${formatGoalValue(afterValue, cfg.unit)} (${afterPct}%)` : `${beforePct}% to goal`}
        </span>
        <span>
          Goal {formatGoalValue(goal.target_value, cfg.unit)}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {hasMeaningfulMove && afterValue !== null && (
          <Button size="sm" onClick={saveProgress} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Target className="h-3.5 w-3.5" />}
            Save progress
          </Button>
        )}
        <Button asChild size="sm" variant="outline" className="gap-1.5">
          <Link to="/goals">View all goals <ArrowRight className="h-3.5 w-3.5" /></Link>
        </Button>
      </div>
    </motion.div>
  );
}
